import { CustomValidationRenderer } from '@rightship-tech/common-ui/client-validation-system/custom-validation-renderer';
import { autoinject, observable, bindable, computedFrom } from "aurelia-framework";
import { ValidationController, ValidationControllerFactory, ValidationRules } from 'aurelia-validation';
import { AgentDetailsModel, PortModel, RightShipInspectionDetailsModel, RightShipInspectionStatusEnum } from '../../../models/rightship-inspections.models';
import { NotificationClientService, NotificationType } from '@rightship-tech/common-ui/services/basic/notification-client.service';
import { RightshipInspectionService } from '../../../services/rightship-inspection.service';
import { ValidationsClientService } from '@rightship-tech/common-ui/services/basic/validations-client.service';
import { Router } from 'aurelia-router';
import { RsInspectionRouteConstants } from '../../../constants/route.constants';
import { GoogleAnalyticsService } from '@rightship-tech/common-ui/services/analytics/google-analytics.service';
import * as moment from "moment";
import { RsInspectionGoogleAnalyticsActionName } from '../../../constants/google-analytics.constants';
import { TitleConst } from '../../../constants/title.constaints';

@autoinject
export class ChangeInspectionDate {
    public inspectionModel: RightShipInspectionDetailsModel;
    public countryId: number;
    public portId: number;
    public days?: number;
    public hours?: number;
    public agent: AgentDetailsModel;
    public readonly format: string = "DD MMM YYYY";
    public readonly title: string = TitleConst.ChangeInspectionDate;
    public agentCountryCode: string = "";
    public agentOfficeNumber: string = "";
    public agentName: string;
    public agentEmailAddress: string;
    public newAgentOfficeNumber: string;
    public newAgentName: string;
    public newAgentEmailAddress: string;
    public selectedPort: PortModel;

    private _estimatedArrivalDate: Date | string = null;

    private _resettingMaxDate: boolean = false;

    @bindable()
    public newInspectionDate: Date = null;

    @observable
    public doubleInpector: boolean = false;

    private _validator: ValidationController;

    private _encryptedData: string;
    private _rsInspectionId: number;

    @computedFrom("_estimatedArrivalDate")
    get estimatedArrivalDate() {
        return this._estimatedArrivalDate;
    }

    set estimatedArrivalDate(value: Date | string) {
        this._resettingMaxDate = true;
        this._estimatedArrivalDate = value;
    }

    @computedFrom("estimatedArrivalDate", "days", "hours", '_resettingMaxDate')
    get maxNewInspectionDate() {
        if (this._resettingMaxDate) {
            setTimeout(() => {
                this._resettingMaxDate = false;
            }, 150);
            return null;
        }
        if (!this.estimatedArrivalDate) return null;
        const maxDate = moment(this.estimatedArrivalDate).add(this.days + (this.hours > 0 ? 1 : 0) - 1, "days").toDate();
        maxDate.setHours(23, 59, 59);
        if (this.newInspectionDate && maxDate < this.newInspectionDate) this.newInspectionDate = null;
        return maxDate;
    }

    @computedFrom("estimatedArrivalDate")
    get minNewInspectionDate() {
        if (!this.estimatedArrivalDate) return null;
        const minDate = new Date(this.estimatedArrivalDate);
        minDate.setHours(0, 0, 0, 0);
        if (this.newInspectionDate && minDate > this.newInspectionDate) this.newInspectionDate = null;
        return minDate;
    }

    @computedFrom("inspectionModel", "inspectionModel.portAndOperationalDetails", "inspectionModel.portAndOperationalDetails.requestedInspectionDate")
    get currentInspectionDate() {
        return this.inspectionModel && this.inspectionModel.portAndOperationalDetails ? this.inspectionModel.portAndOperationalDetails.requestedInspectionDate : null;
    }
    @computedFrom("portId")
    get isPortChanged() {
        return this.portId != this.inspectionModel.portAndOperationalDetails.portId;
    }

    constructor(validationControllerFactory: ValidationControllerFactory,
        private notificationService: NotificationClientService,
        private validationsClientService: ValidationsClientService,
        private router: Router,
        private googleAnalyticsService: GoogleAnalyticsService,
        private rightshipInspectionService: RightshipInspectionService
    ) {
        this._validator = validationControllerFactory.createForCurrentScope();
        this._validator.addRenderer(new CustomValidationRenderer());
    }

    async activate(params: { encryptedData: string }) {
        this._encryptedData = decodeURIComponent(params.encryptedData);
        this.inspectionModel = await this.rightshipInspectionService.getRightShipInspectionDetails(this._encryptedData);
        this._rsInspectionId = this.inspectionModel.rightShipInspectionId;
        if (this.inspectionModel.statusId == RightShipInspectionStatusEnum.Cancelled
            || this.inspectionModel.statusId == RightShipInspectionStatusEnum.Acceptable
            || this.inspectionModel.statusId == RightShipInspectionStatusEnum.Unacceptable
        ) {
            this.router.navigateToRoute(RsInspectionRouteConstants.CloseRequest, { isDeclined: false, encryptedData: encodeURIComponent(this._encryptedData) });
            return;
        }
        if (this.inspectionModel) {
            this.agent = this.inspectionModel.agentDetails;
            this.agentName = this.inspectionModel.agentDetails?.agentName;
            this.agentEmailAddress = this.inspectionModel.agentDetails?.agentEmailAddress;
            this.agentCountryCode = this.getAgentOfficeNumber(this.agent.agentOfficeNumber).code;
            this.agentOfficeNumber = this.getAgentOfficeNumber(this.agent.agentOfficeNumber).number;
        }
    }

    async attached() {
        this.getInspectionDetails();
        this.setUpValidation();
    }

    getAgentOfficeNumber(value: string) {

        // Early return for invalid inputs
        if (!value?.trim()) {
            return { code: '', number: '' };
        }

        // Split on multiple spaces and remove empty elements
        const parts = value.trim().split(" ");

        // If we don't have at least 1 parts, return empty
        if (parts.length < 1) {
            return { code: '', number: '' };
        }

        if (parts.length == 1) {
            return {
                code: '',
                number: parts[0],
            };
        }

        return {
            code: parts[0].replace(/^\+/, ''), // Remove leading + if exists
            number: parts.slice(1).join(''),   // Join remaining parts without spaces
        };
    }

    getAgentOfficeNumberString(code: string, number: string) {
        return code + " " + number;
    }

    getInspectionDetails() {

        //TODO: Implement get invoice details logic
        if (this.inspectionModel && this.inspectionModel.portAndOperationalDetails) {
            this.countryId = this.inspectionModel.portAndOperationalDetails.countryId;
            this.portId = this.inspectionModel.portAndOperationalDetails.portId;
            this._estimatedArrivalDate = this.inspectionModel.portAndOperationalDetails.estimatedArrivalDate;
            this.days = this.parseDaysAndHours(this.inspectionModel.portAndOperationalDetails.lengthOfTimeAtBerth).days;
            this.hours = this.parseDaysAndHours(this.inspectionModel.portAndOperationalDetails.lengthOfTimeAtBerth).hours;
        }
    }

    //Method to parse days and hours from a string
    parseDaysAndHours(input: string): { days: number, hours: number } {
        const daysMatch = input.match(/(\d+)\s*days?/);
        const hoursMatch = input.match(/(\d+)\s*hours?/);

        const days = daysMatch ? parseInt(daysMatch[1], 10) : 0;
        const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;

        return { days, hours };
    }

    setUpValidation() {
        this.getRules();
    }

    getRules() {
        ValidationRules
            .ensure("days")
            .satisfies(() => this.days > 0 || this.hours > 0)
            .withMessage("Please enter Length of time at berth")
            .ensure("newInspectionDate")
            .required()
            .withMessage("Please enter New inspection date")
            .ensure("estimatedArrivalDate")
            .required()
            .withMessage("Please enter Estimated arrival date")
            .ensure("portId")
            .required()
            .withMessage("Please choose port")
            .ensure("countryId")
            .required()
            .withMessage("Please choose country")
            .ensure("agentOfficeNumber")
            .required()
            .withMessage("Please enter Agent office number")
            .ensure("agentName")
            .required()
            .withMessage("Please enter Agent name")
            .ensure("agentEmailAddress")
            .required()
            .withMessage("Please enter Agent email address")
            .on(this);
    }

    async save() {
        const validator = await this._validator.validate();
        if (validator.valid) {
            try {
                //Implement reschedule inspection logic and update request payload
                const response = await this.rightshipInspectionService.rescheduleInspection(this._encryptedData, {
                    newInspectionDate: this.newInspectionDate,
                    countryId: this.countryId,
                    newPortId: this.portId != this.inspectionModel.portAndOperationalDetails.portId ? this.portId : null,
                    newPortName: this.portId != this.inspectionModel.portAndOperationalDetails.portId ? this.selectedPort.name : "",
                    days: this.days,
                    hours: this.hours,
                    estimatedArrivalDate: this.estimatedArrivalDate,
                    newAgentName: this.agentName != this.inspectionModel.agentDetails.agentName ? this.agentName : "",
                    newAgentOfficeNumber: this.getAgentOfficeNumberString(this.agentCountryCode, this.agentOfficeNumber) != this.inspectionModel.agentDetails.agentOfficeNumber
                        ? this.getAgentOfficeNumberString(this.agentCountryCode, this.agentOfficeNumber) : "",
                    newAgentEmailAddress: this.agentEmailAddress != this.inspectionModel.agentDetails.agentEmailAddress ? this.agentEmailAddress : "",
                });

                if (response.validationResult && response.validationResult.hasError) {
                    this.validationsClientService.showValidationErrors(response.validationResult);
                }
                else {
                    this.notificationService.show("Reschedule inspection succeed", "Reschedule inspection succeed", NotificationType.Success);

                    //Send GA4 event
                    if (this.inspectionModel.hasAssignedInspector) {
                        this.googleAnalyticsService.sendCustomGA4EventMultiAnonymously(RsInspectionGoogleAnalyticsActionName.RescheduleInspectionAfterAssigned, {}, this.inspectionModel.emailIdentifier);
                    }
                    else {
                        this.googleAnalyticsService.sendCustomGA4EventMultiAnonymously(RsInspectionGoogleAnalyticsActionName.RescheduleInspectionBeforeAssigned, {}, this.inspectionModel.emailIdentifier);
                    }
                    this.router.navigateToRoute(RsInspectionRouteConstants.SubmittedChangeInspectionDate, {
                        vesselName: this.inspectionModel.vesselDetails.vesselName,
                        vesselImo: this.inspectionModel.vesselDetails.vesselImo,
                        portName: response.portName,
                        country: response.countryName,
                        previousInspectionDate: moment(this.currentInspectionDate).format(this.format),
                        requestedInspectionDate: moment(this.newInspectionDate).format(this.format),
                        rsInspectionId: this._rsInspectionId,
                        encryptedData: encodeURIComponent(this._encryptedData),
                        agentName: this.agentName,
                        agentOfficeNumber: this.getAgentOfficeNumberString(this.agentCountryCode, this.agentOfficeNumber),
                        agentEmailAddress: this.agentEmailAddress
                    });
                }
            } catch (error) {
                this.notificationService.show("Reschedule inspection failed", "Error occurred while rescheduling inspection", NotificationType.Error);
            }
        }
    }

}